import {
    MenuItemTooltip,
    type MenuItem,
    type TabItem,
} from '@ics-portal/guardian';
import { type ReactNode } from 'react';
import { type Maybe } from 'graphql/jsutils/Maybe';
import { InfoTabIcon } from '@components/_atoms/icons';
import InfoCardBulkSelection, {
    type InfoCardBulkSelectionItem,
} from '@components/_molecules/InfoCards/InfoCardBulkSelection';
import TabbedInfoCard from '@components/_molecules/InfoCards/TabbedInfoCard';
import { type RequestRow } from '@pages/notification-components/components/RequestsCard/RequestsTable/columns';
import { type ComponentCreateRequestMapped } from '@pages/notification-components/models/ComponentCreateRequestMapped';
import {
    useRequestMenuActions,
    type RequestMenuAction,
} from '@pages/notification-components/components/RequestsCard/RequestsTable/use-request-actions';
import { humanizeRequestStatus } from '@pages/notification-components/utils/humanizeRequestStatus';
import { RequestDetailsTab } from './components/RequestDetailsTab';

interface RequestInfoCardProps {
    requests: RequestRow[];
    request: Maybe<ComponentCreateRequestMapped>;
}

function getMenuItems(
    requestActions: RequestMenuAction[],
    requests: RequestRow[],
): MenuItem[] {
    return requestActions
        .filter((action) => action.isVisible(requests))
        .map((action) => {
            const disabled = action.disabled(requests);
            const menuItem: MenuItem = {
                id: action.id,
                icon: action.icon,
                label: action.label,
                disabled: Boolean(disabled),
                command: () => {
                    action.onClick(requests);
                },
            };

            if (disabled) {
                menuItem.template = (item) => (
                    <MenuItemTooltip
                        id={`request-action-${action.id}`}
                        tooltip={disabled.tooltip}
                        position="left"
                        item={item}
                    />
                );
            }

            return menuItem;
        });
}

function getBulkSelectionItems(
    requests: RequestRow[],
): InfoCardBulkSelectionItem[] {
    return requests.map((request) => ({
        key: request.id,
        panelHeader: 'Request',
        fields: [
            { label: 'Component', value: request.componentName },
            { label: 'Version', value: request.componentVersion },
            { label: 'Vendor', value: request.componentVendorName ?? '-' },
            {
                label: 'Status',
                value: humanizeRequestStatus(request.statusCode),
            },
        ],
    }));
}

export function RequestInfoCard({
    request,
    requests,
}: RequestInfoCardProps): ReactNode {
    const requestActions = useRequestMenuActions();
    const menuItems = getMenuItems(requestActions, requests);

    if (requests.length === 0) {
        return null;
    }

    if (requests.length > 1) {
        const tabItems: TabItem[] = [
            {
                name: 'Bulk selection',
                icon: <InfoTabIcon />,
                content: (
                    <InfoCardBulkSelection
                        items={getBulkSelectionItems(requests)}
                        actions={menuItems}
                    />
                ),
            },
        ];

        return <TabbedInfoCard tabItems={tabItems} />;
    }

    if (!request) {
        return null;
    }

    const tabItems: TabItem[] = [
        {
            name: 'Details',
            icon: <InfoTabIcon />,
            content: (
                <RequestDetailsTab request={request} actions={menuItems} />
            ),
        },
    ];

    return <TabbedInfoCard tabItems={tabItems} />;
}
